import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Page from '../../../componentes/plantilla/Page';
import Card from '../../../componentes/contenedores/Card';
import { PlanillaServicio } from '../../../servicios/planillaServicio';
import { mostraAlerta, mostraAlertaOk, mostraAlertaPregunta } from '../../../componentes/alerts/sweetAlert';

const GestionPrestamos = () => {
  const [prestamos, setPrestamos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [cargando, setCargando] = useState(true);

  // Estado para el modal de nuevo préstamo
  const [modalNuevo, setModalNuevo] = useState(false);
  const [errores, setErrores] = useState({});
  const [form, setForm] = useState({
    empleadoId: '',
    nombreEmpleado: '',
    tipo: 'Prestamo',
    montoOriginal: '',
    totalCuotas: 10,
    cuotaPeriodica: '',
    motivo: '',
  });

  const datosPage = {
    titulo: {
      titulo: 'Gestión de Préstamos y Anticipos a Colaboradores',
      url: '/app/admin/planillas/prestamos',
      tituloUrl: 'Planillas',
      nombreUrl: 'Préstamos',
    },
  };

  const cargarDatos = async () => {
    try {
      setCargando(true);
      const [listPrestamos, listConfigs] = await Promise.all([
        PlanillaServicio.listarPrestamos(),
        PlanillaServicio.listarConfiguraciones(),
      ]);
      setPrestamos(listPrestamos || []);
      setEmpleados(listConfigs || []);
    } catch (error) {
      console.error('Error cargando préstamos:', error);
      mostraAlerta('Error al cargar la cartera de préstamos', 'error');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const abrirModalNuevo = () => {
    setErrores({});
    const primerEmp = empleados.length > 0 ? empleados[0] : null;
    setForm({
      empleadoId: primerEmp ? primerEmp.empleadoId : '',
      nombreEmpleado: primerEmp ? primerEmp.nombreEmpleado : '',
      tipo: 'Prestamo',
      montoOriginal: '',
      totalCuotas: 10,
      cuotaPeriodica: '',
      motivo: '',
    });
    setModalNuevo(true);
  };

  const cerrarModalNuevo = () => {
    setModalNuevo(false);
    setErrores({});
  };

  const handleMontoChange = (monto, cuotas) => {
    const m = parseFloat(monto) || 0;
    const c = parseInt(cuotas) || 1;
    const cuotaCalc = c > 0 ? Number((m / c).toFixed(2)) : 0;
    setForm((prev) => ({
      ...prev,
      montoOriginal: monto,
      totalCuotas: cuotas,
      cuotaPeriodica: cuotaCalc > 0 ? cuotaCalc : '',
    }));
  };

  const validarFormulario = () => {
    const nuevosErrores = {};
    const monto = parseFloat(form.montoOriginal);
    if (!form.montoOriginal || isNaN(monto) || monto <= 0) {
      nuevosErrores.montoOriginal = 'El monto es obligatorio y debe ser un número positivo.';
    }
    const cuotas = parseInt(form.totalCuotas);
    if (!form.totalCuotas || isNaN(cuotas) || cuotas <= 0) {
      nuevosErrores.totalCuotas = 'El plazo de cuotas debe ser mayor a 0.';
    }
    const cuota = parseFloat(form.cuotaPeriodica);
    if (!form.cuotaPeriodica || isNaN(cuota) || cuota <= 0) {
      nuevosErrores.cuotaPeriodica = 'La cuota periódica debe ser mayor a 0.';
    }
    if (!form.motivo || !form.motivo.trim()) {
      nuevosErrores.motivo = 'El motivo o justificación es obligatorio.';
    }
    if (!form.empleadoId) {
      nuevosErrores.empleadoId = 'Debe seleccionar un colaborador beneficiario.';
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleGuardarPrestamo = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) {
      return;
    }

    const selEmp = empleados.find((em) => String(em.empleadoId) === String(form.empleadoId));
    const nombreCompleto = selEmp?.nombreEmpleado || form.nombreEmpleado || `Colaborador #${form.empleadoId}`;

    const formCompleto = {
      ...form,
      nombreEmpleado: nombreCompleto,
    };

    try {
      await PlanillaServicio.guardarPrestamo(formCompleto);
      mostraAlertaOk('Préstamo o anticipo registrado exitosamente');
      cerrarModalNuevo();
      await cargarDatos();
    } catch (err) {
      mostraAlerta('Error al registrar préstamo', 'error');
    }
  };

  const handleEliminarPrestamo = (id, nombreEmpleado) => {
    mostraAlertaPregunta(
      async (confirmado) => {
        if (confirmado) {
          try {
            await PlanillaServicio.eliminarPrestamo(id);
            mostraAlertaOk('Préstamo eliminado exitosamente');
            await cargarDatos();
          } catch (err) {
            mostraAlerta('Error al eliminar el préstamo', 'error');
          }
        }
      },
      `¿Está seguro de eliminar el registro de préstamo para ${nombreEmpleado}?`,
      'question'
    );
  };

  const formatLempiras = (val) =>
    `L ${Number(val || 0).toLocaleString('es-HN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const totalCarteraPrestamos = prestamos
    .filter((p) => p.estado === 'ACTIVO')
    .reduce((acc, p) => acc + (p.saldoPendiente || 0), 0);

  return (
    <Page datos={datosPage}>
      {/* Botones de Cabecera */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div className="d-flex align-items-center gap-2">
          <Link to="/app/admin/planillas" className="btn btn-outline-secondary btn-sm">
            <i className="fas fa-arrow-left mr-1"></i> Volver a Planillas
          </Link>
          <button className="btn btn-primary shadow-sm font-weight-bold" onClick={abrirModalNuevo}>
            <i className="fas fa-plus-circle mr-1"></i> Otorgar Nuevo Préstamo / Anticipo
          </button>
        </div>
        <div className="text-right">
          <span className="text-muted small d-block">Saldo Total Activo en Cartera Interna:</span>
          <span className="font-weight-bold text-primary h5 my-0">{formatLempiras(totalCarteraPrestamos)}</span>
        </div>
      </div>

      <Card
        titulo="Cartera Interna de Préstamos a Colaboradores"
        subtitulo="Los préstamos con saldo pendiente se deducen automáticamente en cada generación y aprobación de planilla."
      >
        {cargando ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2">Cargando préstamos...</p>
          </div>
        ) : prestamos.length === 0 ? (
          <div className="text-center py-5 rounded border" style={{ backgroundColor: 'var(--n8-panel, rgba(255,255,255,0.03))' }}>
            <i className="fas fa-hand-holding-usd text-muted" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 font-weight-bold">No hay préstamos activos registrados</h5>
            <p className="text-muted">No se registran deducciones por préstamos pendientes.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="thead-dark">
                <tr>
                  <th>Colaborador</th>
                  <th>Tipo / Motivo</th>
                  <th className="text-right">Monto Original</th>
                  <th className="text-right">Cuota Periódica</th>
                  <th className="text-center" style={{ minWidth: '150px' }}>
                    Progreso Cuotas
                  </th>
                  <th className="text-right">Saldo Pendiente</th>
                  <th className="text-center">Estado</th>
                  <th className="text-center" style={{ width: '80px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {prestamos.map((pr) => {
                  const pct = pr.totalCuotas > 0 ? Math.round((pr.cuotasPagadas / pr.totalCuotas) * 100) : 0;
                  const esAnticipo = pr.tipo === 'Anticipo' || (pr.motivo && pr.motivo.toLowerCase().includes('anticipo'));
                  return (
                    <tr key={pr.id}>
                      <td>
                        <div className="font-weight-bold">{pr.nombreEmpleado}</div>
                        <small className="text-muted">Inicio: {pr.fechaInicio}</small>
                      </td>
                      <td>
                        <span className={`badge ${esAnticipo ? 'badge-info' : 'badge-primary'} mr-1`}>
                          {esAnticipo ? 'Anticipo' : 'Préstamo'}
                        </span>
                        <span className="text-muted">{pr.motivo}</span>
                      </td>
                      <td className="text-right font-weight-bold">{formatLempiras(pr.montoOriginal)}</td>
                      <td className="text-right text-danger font-weight-bold">
                        -{formatLempiras(pr.cuotaPeriodica)}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <small className="text-muted font-weight-bold">
                            {pr.cuotasPagadas}/{pr.totalCuotas}
                          </small>
                          <div className="progress flex-grow-1" style={{ height: '8px', minWidth: '80px' }}>
                            <div
                              className={`progress-bar ${pr.estado === 'ACTIVO' ? 'bg-primary' : 'bg-success'}`}
                              role="progressbar"
                              style={{ width: `${pct}%` }}
                              aria-valuenow={pct}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                          <small className="text-muted">{pct}%</small>
                        </div>
                      </td>
                      <td className="text-right font-weight-bold text-danger" style={{ fontSize: '1rem' }}>
                        {formatLempiras(pr.saldoPendiente)}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            pr.estado === 'ACTIVO' ? 'badge-warning text-dark' : 'badge-success'
                          } px-2 py-1`}
                        >
                          {pr.estado}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Eliminar o cancelar registro"
                          onClick={() => handleEliminarPrestamo(pr.id, pr.nombreEmpleado)}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal: Registrar Nuevo Préstamo */}
      {modalNuevo && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)' }}
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content shadow border-0">
              <form onSubmit={handleGuardarPrestamo} noValidate>
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title font-weight-bold">
                    <i className="fas fa-hand-holding-usd mr-2"></i> Nuevo Préstamo o Anticipo
                  </h5>
                  <button type="button" className="close text-white" onClick={cerrarModalNuevo}>
                    <span>&times;</span>
                  </button>
                </div>

                <div className="modal-body p-4">
                  <div className="row">
                    <div className="col-md-5 mb-3">
                      <label className="font-weight-bold">Tipo de Solicitud *</label>
                      <select
                        className="form-control"
                        value={form.tipo}
                        onChange={(e) => {
                          const nuevoTipo = e.target.value;
                          const cuotasSugeridas = nuevoTipo === 'Anticipo' ? 2 : 10;
                          setForm((prev) => ({
                            ...prev,
                            tipo: nuevoTipo,
                          }));
                          handleMontoChange(form.montoOriginal, cuotasSugeridas);
                        }}
                      >
                        <option value="Prestamo">Préstamo Interno (Mediano plazo)</option>
                        <option value="Anticipo">Anticipo de Sueldo (Corto plazo)</option>
                      </select>
                    </div>

                    <div className="col-md-7 mb-3">
                      <label className="font-weight-bold">Colaborador Beneficiario *</label>
                      <select
                        className={`form-control ${errores.empleadoId ? 'is-invalid border-danger' : ''}`}
                        value={form.empleadoId}
                        onChange={(e) => {
                          const selEmp = empleados.find((em) => em.empleadoId === Number(e.target.value));
                          setForm((prev) => ({
                            ...prev,
                            empleadoId: e.target.value,
                            nombreEmpleado: selEmp ? selEmp.nombreEmpleado : '',
                          }));
                          if (errores.empleadoId) {
                            setErrores((prev) => ({ ...prev, empleadoId: null }));
                          }
                        }}
                      >
                        {empleados.map((em) => (
                          <option key={em.empleadoId} value={em.empleadoId}>
                            {em.nombreEmpleado} — {em.cargo}
                          </option>
                        ))}
                      </select>
                      {errores.empleadoId && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle mr-1"></i> {errores.empleadoId}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="font-weight-bold">Monto Total a Otorgar (L.) *</label>
                      <div className="position-relative">
                        <input
                          type="number"
                          step="any"
                          className={`form-control font-weight-bold ${errores.montoOriginal ? 'is-invalid border-danger pr-4' : ''}`}
                          placeholder="Ej: 2000"
                          value={form.montoOriginal}
                          onChange={(e) => {
                            handleMontoChange(e.target.value, form.totalCuotas);
                            if (errores.montoOriginal) {
                              setErrores((prev) => ({ ...prev, montoOriginal: null }));
                            }
                          }}
                        />
                        {errores.montoOriginal && (
                          <span className="position-absolute text-danger" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <i className="far fa-exclamation-circle" style={{ fontSize: '1.1rem' }}></i>
                          </span>
                        )}
                      </div>
                      {errores.montoOriginal && (
                        <div className="text-danger small mt-1">
                          {errores.montoOriginal}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="font-weight-bold">Plazo (Total de Cuotas) *</label>
                      <div className="position-relative">
                        <input
                          type="number"
                          min="1"
                          max="60"
                          step="1"
                          className={`form-control ${errores.totalCuotas ? 'is-invalid border-danger pr-4' : ''}`}
                          value={form.totalCuotas}
                          onChange={(e) => {
                            handleMontoChange(form.montoOriginal, e.target.value);
                            if (errores.totalCuotas) {
                              setErrores((prev) => ({ ...prev, totalCuotas: null }));
                            }
                          }}
                        />
                        {errores.totalCuotas && (
                          <span className="position-absolute text-danger" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <i className="far fa-exclamation-circle" style={{ fontSize: '1.1rem' }}></i>
                          </span>
                        )}
                      </div>
                      {errores.totalCuotas && (
                        <div className="text-danger small mt-1">
                          {errores.totalCuotas}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="font-weight-bold text-danger">Cuota a Deducir por Período (L.) *</label>
                    <div className="position-relative">
                      <input
                        type="number"
                        step="any"
                        className={`form-control font-weight-bold text-danger ${errores.cuotaPeriodica ? 'is-invalid border-danger pr-4' : ''}`}
                        placeholder="Monto por planilla"
                        value={form.cuotaPeriodica}
                        onChange={(e) => {
                          setForm((prev) => ({ ...prev, cuotaPeriodica: e.target.value }));
                          if (errores.cuotaPeriodica) {
                            setErrores((prev) => ({ ...prev, cuotaPeriodica: null }));
                          }
                        }}
                      />
                      {errores.cuotaPeriodica && (
                        <span className="position-absolute text-danger" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                          <i className="far fa-exclamation-circle" style={{ fontSize: '1.1rem' }}></i>
                        </span>
                      )}
                    </div>
                    {errores.cuotaPeriodica ? (
                      <div className="text-danger small mt-1">
                        {errores.cuotaPeriodica}
                      </div>
                    ) : (
                      <small className="text-muted">
                        Se deducirá automáticamente en cada período de planilla hasta completar el saldo.
                      </small>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="font-weight-bold">Motivo / Justificación *</label>
                    <textarea
                      className={`form-control ${errores.motivo ? 'is-invalid border-danger' : ''}`}
                      rows="2"
                      placeholder="Ej: Anticipo para gastos escolares / reparación de vehículo"
                      value={form.motivo}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, motivo: e.target.value }));
                        if (errores.motivo) {
                          setErrores((prev) => ({ ...prev, motivo: null }));
                        }
                      }}
                    ></textarea>
                    {errores.motivo && (
                      <div className="text-danger small mt-1">
                        <i className="fas fa-exclamation-circle mr-1"></i> {errores.motivo}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={cerrarModalNuevo}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary font-weight-bold">
                    <i className="fas fa-check-circle mr-1"></i> Registrar Préstamo
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default GestionPrestamos;
